import React from "react"
import autobind from "class-autobind"
import Table from "../../common/Table"
import { TableHeader, TableHeaderColumn, TableRow, TableBody, TableRowColumn, FloatingActionButton, FontIcon } from "material-ui"
import ContentAdd from "material-ui/svg-icons/content/add"
import DeleteIcon from "material-ui/svg-icons/action/delete"
import strings from "../../../localization/strings"
import { IPayClass } from "../../../models"
import * as payGroupAndTypeHelper from "../../../helpers/payGroupAndTypeHelper"
import AddPayClassDialog from "./AddPayClassDialog"
import AppStateStore from "../../../stores/AppStateStore"
import { red500, green500 } from "material-ui/styles/colors"
import * as permissionHelper from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import AccountStore from "../../../stores/AccountStore"
import _ from "lodash"

interface IProps {
    readonly?: boolean
    payClasses: IPayClass[]
    availablePayTypeIds: string[]
    handleChange(payClasses: IPayClass[]): void
}
interface IState {
    payClasses: IPayClass[]
    selectedPayClasses: IPayClass[]
    editPayClass: IPayClass
    openAddPayClassDialog: boolean
    showTable: boolean
}

export default class AvailablePayClasses extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        this.state = {
            payClasses: props.payClasses,
            selectedPayClasses: [],
            editPayClass: null,
            openAddPayClassDialog: false,
            showTable: true,
        }
        autobind(this)
    }

    handleChange(payClasses: IPayClass[]) {
        this.setState({ payClasses, selectedPayClasses: [], showTable: false }, () => this.setState({ showTable: true }))
        this.props.handleChange(payClasses)
    }

    render() {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        })
        return (
            <div style={{ position: "relative" }}>
                {!this.props.readonly &&
                    <div style={{ position: "absolute", top: -45, right: 0, zIndex: 998 }}>
                        <FloatingActionButton
                            data-intercom-target={"Payclass-Btn-AddOrDelete"}
                            secondary={this.state.selectedPayClasses.length === 0}
                            onTouchTap={() =>
                                this.state.selectedPayClasses.length ?
                                    AppStateStore.setConfirmDialogContent({
                                        content: strings.Are_you_sure_you_want_to_delete_selected_PayClasses,
                                        confirm: () =>
                                            this.handleChange(this.state.payClasses.filter(item => this.state.selectedPayClasses.find(selectedItem => selectedItem.Id === item.Id) ? false : true))
                                    }) :
                                    this.setState({ openAddPayClassDialog: true })}
                        >
                            {this.state.selectedPayClasses.length ?
                                <DeleteIcon /> :
                                <ContentAdd />
                            }
                        </FloatingActionButton>
                    </div>
                }
                {this.state.showTable &&
                    <Table
                        readonly={this.props.readonly}
                        allowClickRow
                        items={this.state.payClasses}
                        handleClickRow={editPayClass => this.setState({ editPayClass, openAddPayClassDialog: true })}
                        handleSelect={selectedPayClasses => this.setState({ selectedPayClasses })}
                    >
                        <TableHeader>
                            <TableRow className="ShortSelectBoxTableHeader">
                                <TableHeaderColumn className="TextOverflow" title={strings.Name} style={{ textAlign: "left", width: 100, paddingLeft: 5, paddingRight: 5 }}>{strings.Name}</TableHeaderColumn>
                                {permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ViewRates) && [
                                    <TableHeaderColumn key="BillRate" className="TextOverflow" title={strings.Bill_Rate} style={{ textAlign: "left", paddingLeft: 5, paddingRight: 5 }}>{strings.Bill_Rate}</TableHeaderColumn>,
                                    <TableHeaderColumn key="PayRate" className="TextOverflow" title={strings.Pay_Rate} style={{ textAlign: "left", paddingLeft: 5, paddingRight: 5 }}>{strings.Pay_Rate}</TableHeaderColumn>,
                                    <TableHeaderColumn key="Burder" className="TextOverflow" title={strings.Burden} style={{ textAlign: "left", paddingLeft: 5, paddingRight: 5 }}>{strings.Burden}</TableHeaderColumn>
                                ]}
                                <TableHeaderColumn className="TextOverflow" title={strings.Pay_Type} style={{ textAlign: "left", paddingLeft: 5, paddingRight: 5 }}>{strings.Pay_Type}</TableHeaderColumn>
                                <TableHeaderColumn className="TextOverflow" title={strings.Code} style={{ textAlign: "left", width: 100, paddingLeft: 5, paddingRight: 5 }}>{strings.Code}</TableHeaderColumn>
                                {AccountStore.getUseBasePayRateInPayClassEnabled() &&
                                    <TableHeaderColumn className="TextOverflow" title={strings.Using_Base_Pay_Rate} style={{ textAlign: "center", width: "auto", paddingLeft: 5, paddingRight: 5 }}>{strings.Using_Base_Pay_Rate}</TableHeaderColumn>
                                }
                                {AccountStore.getUseBasePayRateInPayClassEnabled() &&
                                    <TableHeaderColumn className="TextOverflow" title={strings.Pay_Rate_Add} style={{ textAlign: "center", width: "auto", paddingLeft: 5, paddingRight: 5 }}>{strings.Pay_Rate_Add}</TableHeaderColumn>
                                }
                                {AccountStore.getUseBasePayRateInPayClassEnabled() &&
                                    <TableHeaderColumn className="TextOverflow" title={strings.Pay_Rate_Percent} style={{ textAlign: "center", width: "auto", paddingLeft: 5, paddingRight: 5 }}>{strings.Pay_Rate_Percent}</TableHeaderColumn>
                                }
                                {AccountStore.getShowPayClassOnESS() &&
                                    <TableHeaderColumn className="TextOverflow" title={strings.Hide_On_ESS} style={{ textAlign: "center", width: 75, paddingLeft: 5, paddingRight: 5 }}>{strings.Hide_On_ESS}</TableHeaderColumn>
                                }
                            </TableRow>
                        </TableHeader>
                        <TableBody showRowHover={true} deselectOnClickaway={false}>
                            {this.state.payClasses.map((payClass, index) => {
                                const payTypeName = payGroupAndTypeHelper.getPayTypeNameById(payClass.PayTypeId)
                                return (
                                    <TableRow
                                        data-intercom-target={index === 0 && "Payclass-openItemDetails"}
                                        className="ShortSelectBoxTableRow"
                                        key={payClass.Id}
                                        selected={this.state.selectedPayClasses.find(item => item.Id === payClass.Id) ? true : false}
                                    >
                                        <TableRowColumn style={{ textAlign: "left", cursor: "pointer", width: 100, paddingLeft: 5, paddingRight: 5 }}>{payClass.Name}</TableRowColumn>
                                        {permissionHelper.hasPermission(AuthenticationStore.Permissions.Account.ViewRates) && [
                                            <TableRowColumn key="BillRate" style={{ textAlign: "left", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>{payClass.PayInfo.BillRate >= 0 && formatter.format(payClass.PayInfo.BillRate)}</TableRowColumn>,
                                            <TableRowColumn key="PayRate" style={{ textAlign: "left", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>{payClass.PayInfo.PayRate >= 0 && formatter.format(payClass.PayInfo.PayRate)}</TableRowColumn>,
                                            <TableRowColumn key="Burden" style={{ textAlign: "left", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>{payClass.PayInfo.Burden >= 0 && `${payClass.PayInfo.Burden}%`}</TableRowColumn>
                                        ]}
                                        <TableRowColumn style={{ textAlign: "left", cursor: "pointer", color: payTypeName === strings.invalid && red500, paddingLeft: 5, paddingRight: 5 }}>{payTypeName}</TableRowColumn>
                                        <TableRowColumn style={{ textAlign: "left", cursor: "pointer", width: 100, paddingLeft: 5, paddingRight: 5 }}>{payClass.Code}</TableRowColumn>
                                        
                                        
                                       
                                            {AccountStore.getUseBasePayRateInPayClassEnabled() &&<TableRowColumn style={{ textAlign: "center", cursor: "pointer", width:"auto", paddingLeft: 5, paddingRight: 5 }}>
                                               {payClass.PayInfo.UseEmployeeBasePayRate &&
                                                    <FontIcon className="fa fa-check" style={{ fontSize: 18, color: green500 }} />
                                               }
                                            </TableRowColumn>}
                                        
                                        
                                            {AccountStore.getUseBasePayRateInPayClassEnabled() &&<TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: "auto", paddingLeft: 5, paddingRight: 5 }}>
                                                
                                                    {payClass.PayInfo.UseEmployeeBasePayRate && payClass.PayInfo.BasePayRateAddition >= 0 && formatter.format(payClass.PayInfo.BasePayRateAddition)}
                                               
                                            </TableRowColumn>}
                                         
                                            {AccountStore.getUseBasePayRateInPayClassEnabled() &&
                                            <TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: "auto", paddingLeft: 5, paddingRight: 5 }}>
                                                
                                                    { payClass.PayInfo.UseEmployeeBasePayRate && payClass.PayInfo.BasePayRateAdditionPercentage >= 0 ? `${payClass.PayInfo.BasePayRateAdditionPercentage}%` : "-"}
                                               
                                            </TableRowColumn>
                                         }
                                        {AccountStore.getShowPayClassOnESS() &&
                                            <TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: 75, paddingLeft: 5, paddingRight: 5 }}>
                                                {payClass.HideOnESS &&
                                                    <FontIcon className="fa fa-check" style={{ fontSize: 18, color: green500 }} />
                                                }
                                            </TableRowColumn>
                                        }
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                }
                {!this.state.payClasses.length &&
                    <div style={{ textAlign: "center", marginTop: 25, color: red500 }}>
                        {strings.No_PayClass_found}
                    </div>
                }
                {this.state.openAddPayClassDialog &&
                    <AddPayClassDialog
                        readonly={this.props.readonly}
                        editPayClass={this.state.editPayClass}
                        existingPayClasses={this.state.payClasses}
                        availablePayTypeIds={this.props.availablePayTypeIds}
                        handleAdd={payClass => {
                            this.setState({ openAddPayClassDialog: false })
                            this.handleChange([...this.state.payClasses, payClass])
                        }}
                        handleUpdate={payClass => {
                            const updatedPayClasses = this.state.payClasses.map((item) => {
                                if (payClass.Id === item.Id) {
                                    return {...item,...payClass}
                                }
                                return item
                            })
                            this.setState({payClasses:updatedPayClasses})
                            this.setState({ openAddPayClassDialog: false, editPayClass: null })
                            this.handleChange(updatedPayClasses)
                        }}
                        handleClose={() => this.setState({ openAddPayClassDialog: false, editPayClass: null })}
                    />
                }
            </div >
        )
    }
}